import React, {
  useState, useEffect, useMemo, useCallback, useRef
} from 'react';
import { createPortal } from 'react-dom';
import { Typography } from '@one-thd/sui-atomic-components';
import {
  bool, string, node, number, shape, instanceOf, object
} from 'prop-types';
import debounce from 'lodash/debounce';
import { useDrawerDimensions } from '../../hooks/useDrawerDimensions';

/**
 * DrawerTipMessage - A simple contextual tip that shows when the drawer is open
 *
 * Features:
 * - Portal-based rendering for proper layering
 * - Animated fade in/out using SUI's Fade component
 * - Automatically shows/hides with drawer state
 * - No dismissal logic - tied to drawer lifecycle
 *
 * @param {boolean} show - Whether to show the onboarding tip
 * @param {string|React.Node} title - Main title text for the tip
 * @param {string|React.Node} content - Content/description for the tip
 * @param {number} timeout - Animation duration in milliseconds
 * @param {shape} drawerRef - Ref to the drawer element
 */
const DrawerTipMessage = React.memo(({
  show,
  title,
  content,
  timeout = 400,
  drawerRef,
}) => {
  const [viewportWidth, setViewportWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1280);

  // Track viewport width changes with debouncing
  useEffect(() => {
    const handleResize = debounce(() => {
      setViewportWidth(window.innerWidth);
    }, 100);

    window.addEventListener('resize', handleResize, { passive: true });

    return () => {
      window.removeEventListener('resize', handleResize);
      handleResize.cancel();
    };
  }, [viewportWidth, setViewportWidth]);

  const hookResult = useDrawerDimensions(drawerRef, {
    debounceMs: 16,
    threshold: 5,
    enabled: show && viewportWidth >= 1280,
  });

  // Handle case where hook return undefined
  const { rightPosition, isPositioned } = hookResult || { rightPosition: 0, isPositioned: false };

  const ArrowSvg = useMemo(() => (
    <svg
      viewBox="0 0 271 47"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ width: '250px' }}
    >
      <path
        d={
          'M271 23.094L231 -1.14441e-05V46.188L271 23.094Z'
          + 'M0 23.0942V27.0942C71.957 27.0942 132.588 27.094 235.013 27.094V23.094V19.094'
          + 'C132.588 19.094 71.957 19.0942 0 19.0942V23.0942Z'
        }
        fill="url(#paint0_linear_2170_45788)"
      />
      <defs>
        <linearGradient
          id="paint0_linear_2170_45788"
          x1="-8.32058e-08"
          y1="33.5198"
          x2="271"
          y2="33.5198"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="white" stopOpacity="0" />
          <stop offset="0.365385" stopColor="white" />
        </linearGradient>
      </defs>
    </svg>
  ), []);

  const containerStyle = useMemo(() => {
    // Calculate translateX value instead of right position
    const translateX = -rightPosition; // Negative because we're moving left from the right edge

    return {
      right: 0, // Position at right edge as starting point
      transform: `translate(${translateX}px, -50%)`,
      maxWidth: `calc(100vw - ${rightPosition + 20}px)`,
    };
  }, [rightPosition]);

  // Early return to prevent rendering when not shown
  if (!show || !isPositioned || viewportWidth < 1280) {
    return null;
  }

  const tipContent = (
    <div
      className={
        'sui-fixed sui-top-1/2 sui-transform sui--translate-y-1/2 transform-gpu '
        + 'sui-z-[1500] sui-max-w-sm sui-pointer-events-none sui-will-change-[transform,opacity] '
        + 'sui-transition-opacity sui-duration-200 sui-ease-in-out '
      }
      style={
        {
          ...containerStyle,
          contain: 'layout style paint' // CSS containment
        }
      }
    >
      <div
        className={
          'sui-pointer-events-auto sui-flex sui-flex-col '
            + 'min-[960px]:sui-flex-row min-[960px]:sui-items-center sui-flex-wrap'
        }
      >
        <div className="sui-flex sui-flex-col sui-gap-4">
          <Typography
            variant="h1"
            weight="bold"
            color="inverse"
          >
            {title}
          </Typography>
          <Typography
            variant="body-base"
            className="sui-text-white sui-leading-relaxed"
          >
            {content}
          </Typography>
        </div>
        <div className="min-[960px]:sui-ml-12">
          {ArrowSvg}
        </div>
      </div>
    </div>
  );

  return show ? createPortal(tipContent, document.body) : null;
});

const isBrowser = typeof window !== 'undefined'; // Checking for browser environment, getting Element not found errors.
DrawerTipMessage.propTypes = {
  show: bool.isRequired,
  title: node.isRequired,
  content: node.isRequired,
  timeout: number,
  drawerRef: isBrowser ? shape({
    current: instanceOf(Element)
  }) : object,
};

DrawerTipMessage.defaultProps = {
  timeout: 400, // Drawer Fade animation takes 350ms, adding 50ms buffer
  drawerRef: null

};
export default DrawerTipMessage;